import { ReactElement } from "react";
import { Summary } from "../components/summary/Summary";
import { Particulars } from "../components/tab-sections/Particulars";
import { NotableItems } from "../components/NotableItems";
import { Grading } from "../components/tab-sections/Grading";
import { Documents } from "../components/tab-sections/Documents";
import { Images } from "../components/images/Images";
import { GradeComparison } from "../components/tab-sections/GradeComparison";
import { VesselInspectionDataResponse } from "../types/responses/VesselInspectionDataResponse";
import { Defects } from "../components/Defects";
import { Decarbonisation } from "../components/tab-sections/Decarbonisation";
import { AdjustedGrading } from "../components/tab-sections/AdjustedGrading";
import { hasAdjustedGradingSupplement } from "../utils/DocumentHelpers";
import { APIGrade } from "@idwal/idwal-react-components/dist/types/APIGrade";
import { canShowDataSharingOptIn } from "../utils/dataSharingHelpers";
import { DataSharingStatus } from "../constants/DataSharingConstants";
import {
    InspectionTabKey,
    resolveInspectionTabs
} from "../config/inspectionTabLayouts";

export const getTabsForInspection = (
    vesselInspectionDataResponse: VesselInspectionDataResponse,
    documentData: any,
    t: any,
    dataSharingStatusOverride?: DataSharingStatus | null
) => {
    const vesselInspectionResponse =
        vesselInspectionDataResponse.vesselInspectionResponse;
    if (vesselInspectionResponse == undefined) {
        return [];
    }

    const { tabs, idReport, regimeCodesOnly } = resolveInspectionTabs(
        vesselInspectionResponse.InspectionType,
        {
            isFullReport: !vesselInspectionResponse.earlyRelease,
            hasAdjustedGrading: !!hasAdjustedGradingSupplement(
                documentData?.documents
            )
        }
    );

    const lockTabs = canShowDataSharingOptIn(
        dataSharingStatusOverride,
        vesselInspectionResponse.dataSharingAvailable,
        vesselInspectionResponse.InspectionTypeShortName
    );
    const supplementsTabIndex = tabs.findIndex(
        (tab) => tab.key === "supplements"
    );

    const renderTab = (key: InspectionTabKey): ReactElement => {
        switch (key) {
            case "summary":
                return (
                    <Summary
                        documentData={documentData}
                        vesselInspectionResponse={vesselInspectionResponse}
                        dataSharingStatusOverride={dataSharingStatusOverride}
                    />
                );
            case "particulars":
                return (
                    <Particulars
                        data={vesselInspectionResponse}
                        isLoading={vesselInspectionDataResponse.isLoading}
                        error={vesselInspectionDataResponse.error}
                        isError={vesselInspectionDataResponse.isError}
                    />
                );
            case "notable_items":
                return <NotableItems />;
            case "grade_comparison":
                return (
                    <GradeComparison
                        imo={vesselInspectionResponse.IMONumber}
                        vesselGrade={
                            {
                                grade: vesselInspectionResponse.Grade
                            } as APIGrade
                        }
                    />
                );
            case "grading":
                return (
                    <Grading
                        idReport={idReport}
                        inspectionType={vesselInspectionResponse.InspectionType}
                    />
                );
            case "defects":
                return <Defects regimeCodesOnly={regimeCodesOnly} />;
            case "decarbonisation":
                return <Decarbonisation />;
            case "documents":
                return <Documents linkTabIndex={supplementsTabIndex} />;
            case "supplements":
                return <Documents supplements />;
            case "images":
                return <Images />;
            case "adjusted_grading":
                return <AdjustedGrading />;
        }
    };

    return tabs.map((tab) => ({
        title: t(tab.titleKey),
        content: renderTab(tab.key),
        disabled: tab.key !== "summary" && lockTabs === true
    }));
};
